/**
 * Customer-facing copy helpers shared by Scout tool renderers and the todo checklist.
 * Strips internal identifiers and technical phrasing before showing text to users.
 */

import type { ScoutTodoItem } from "./scoutTodoSnapshot";

const DOMAIN_ACRONYMS: Record<string, string> = {
  ca: "CA",
  ny: "NY",
  us: "US",
  uk: "UK",
  eu: "EU",
  epa: "EPA",
  rcra: "RCRA",
  epcra: "EPCRA",
  cercla: "CERCLA",
  caa: "CAA",
  cwa: "CWA",
  cfr: "CFR",
  ccr: "CCR",
  nycrr: "NYCRR",
  serc: "SERC",
  lepc: "LEPC",
  lqg: "LQG",
  sqg: "SQG",
  vsqg: "VSQG",
  sds: "SDS",
  cas: "CAS",
  ehs: "EHS",
  tpq: "TPQ",
  npdes: "NPDES",
  ash: "Ash",
  scout: "Scout",
  encamp: "Encamp",
};

const DOMAIN_PHRASES: Array<{ from: string[]; to: string }> = [
  { from: ["tier", "ii"], to: "Tier II" },
  { from: ["tier", "i", "i"], to: "Tier II" },
  { from: ["tier", "iii"], to: "Tier III" },
];

const TECHNICAL_TOKEN_PATTERN =
  /\b(?:omnisearch|tenantid|facilityid|personid|page|pagesize|graphql|lookup|prisma|reportingfacility|facilitychemical|validation-rule)\b/i;

const GENERIC_TODO_LABEL = "Research step";

function applyDomainPhrases(tokens: string[]): string[] {
  const lowered = tokens.map((t) => t.toLowerCase());
  const out: string[] = [];
  let i = 0;
  outer: while (i < tokens.length) {
    for (const { from, to } of DOMAIN_PHRASES) {
      if (lowered.slice(i, i + from.length).join(" ") === from.join(" ")) {
        out.push(to);
        i += from.length;
        continue outer;
      }
    }
    out.push(tokens[i]);
    i += 1;
  }
  return out;
}

function applyDomainAcronyms(token: string): string {
  return DOMAIN_ACRONYMS[token.toLowerCase()] ?? token;
}

function tokenize(name: string): string[] {
  return name
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
}

export function humanizeIdentifier(value: string): string {
  const tokens = tokenize(value);
  if (tokens.length === 0) return value;
  const phrased = applyDomainPhrases(tokens);
  const cased = phrased.map((token, idx) => {
    if (token.includes(" ")) return token;
    const acronym = applyDomainAcronyms(token);
    if (acronym !== token) return acronym;
    if (idx === 0) return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
    return token.toLowerCase();
  });
  return cased.join(" ");
}

function parseFilterQuery(search: string): Map<string, string> {
  const filters = new Map<string, string>();
  const matches = search.matchAll(/([A-Za-z][A-Za-z0-9_]*):("[^"]+"|[^ ]+)/g);
  for (const match of matches) {
    const [, key, rawValue] = match;
    if (!key || !rawValue) continue;
    filters.set(key, rawValue.replace(/"/g, ""));
  }
  return filters;
}

function normalizeStateList(raw: string): string {
  return raw
    .split(/[|,]/)
    .map((state) => state.trim())
    .filter(Boolean)
    .join(", ");
}

export function sanitizeApiLookupQuery(query: string | null): string | null {
  if (!query) return null;
  const trimmed = query.trim();
  if (!trimmed) return null;

  const filters = parseFilterQuery(trimmed);
  if (filters.size > 0) {
    const state = filters.get("state");
    if (state) {
      const states = normalizeStateList(state);
      return states ? `facilities in ${states}` : "facilities";
    }
    if (filters.has("facilityId")) return "a facility";
    if (filters.has("ids")) return "selected facilities";
    if (filters.has("tenantId")) return "facilities";
  }

  const lowered = trimmed.toLowerCase();
  if (TECHNICAL_TOKEN_PATTERN.test(trimmed)) {
    if (lowered.includes("facilit")) return "facilities";
    if (lowered.includes("report")) return "reports";
    if (lowered.includes("task")) return "tasks";
    return null;
  }

  return trimmed;
}

export function sanitizeComplianceSearchQuery(query: string | null): string | null {
  if (!query) return null;
  const trimmed = query.trim();
  if (!trimmed) return null;

  if (
    /\b(?:compliance:[a-z0-9-]+:[A-Za-z0-9._-]+|validation-rule|tenantid|facilityid|personid|omnisearch)\b/i.test(
      trimmed
    )
  ) {
    return null;
  }

  return trimmed;
}

export function isInternalComplianceIdentifier(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (/^compliance:[a-z0-9-]+:[A-Za-z0-9._-]+$/i.test(trimmed)) return true;
  if (trimmed.includes(":validation-rule:")) return true;
  if (trimmed.split(":").length >= 3) return true;
  if (/\b[a-z]+\.[a-z]+\.[a-z0-9._-]+\.[0-9a-f]{6,}\b/i.test(trimmed)) return true;
  return false;
}

export function sanitizeComplianceIdentifier(value: string | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (isInternalComplianceIdentifier(trimmed)) return null;
  return trimmed;
}

export function firstFriendlyComplianceIdentifier(candidates: Array<string | null>): string | null {
  for (const candidate of candidates) {
    const friendly = sanitizeComplianceIdentifier(candidate);
    if (friendly) return friendly;
  }
  return null;
}

/** Customer-facing labels for regulation corpora (matches search tool copy). */
const KNOWLEDGE_CORPUS_LABELS: Record<string, string> = {
  regulations: "federal regulations",
  "ca-regulations": "California regulations",
  "ny-regulations": "New York regulations",
};

const KNOWLEDGE_NODE_TYPE_LABELS: Record<string, string> = {
  section: "Section",
  part: "Part",
  subpart: "Subpart",
  title: "Title",
  chapter: "Chapter",
  division: "Division",
  article: "Article",
};

export type KnowledgeNodeLookupParts = {
  corpusLabel: string;
  /** e.g. "Section 370.10" — omitted when the id has no usable type/name. */
  refLabel: string | null;
};

/**
 * True when `value` looks like a knowledge corpus node id
 * (`regulations:section:40.370.10`), not a human citation.
 */
export function isKnowledgeNodeId(value: string): boolean {
  return /^[a-z][a-z0-9-]*:[a-z][a-z0-9-]*:.+$/i.test(value.trim());
}

function parseKnowledgeNodeId(
  value: string
): { corpus: string; type: string; rest: string } | null {
  const trimmed = value.trim();
  const match = /^([a-z][a-z0-9-]*):([a-z][a-z0-9-]*):(.+)$/i.exec(trimmed);
  if (!match?.[1] || !match[2] || !match[3]) return null;
  return {
    corpus: match[1].toLowerCase(),
    type: match[2].toLowerCase(),
    rest: match[3],
  };
}

function splitNodeIdRest(rest: string): string[] {
  // Canonical ids join parts with `.`; some fixtures still use `:`.
  return rest
    .split(/[.:]/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

function lastMeaningfulSegment(parts: string[]): string | null {
  for (let i = parts.length - 1; i >= 0; i -= 1) {
    const part = parts[i];
    if (part) return part;
  }
  return null;
}

/**
 * Turn the id rest into a short reference name for the given node type.
 * CFR: `40.370.10` + section → `370.10`. CA section hyphens restored to dots.
 */
function formatKnowledgeNodeRefName(corpus: string, type: string, rest: string): string | null {
  const parts = splitNodeIdRest(rest);
  if (parts.length === 0) return null;

  if (corpus === "regulations") {
    if (type === "title") return parts[0] ?? null;
    if (type === "part") return parts[1] ?? lastMeaningfulSegment(parts);
    if (type === "subpart") return parts[2] ?? lastMeaningfulSegment(parts);
    if (type === "section") {
      if (parts.length >= 2) return parts.slice(1).join(".");
      return parts[0] ?? null;
    }
  }

  const last = lastMeaningfulSegment(parts);
  if (!last) return null;

  // CA section ids store dots as hyphens (`66260-10` → `66260.10`).
  if (corpus === "ca-regulations" && type === "section" && /^\d+(-\d+)+$/.test(last)) {
    return last.replace(/-/g, ".");
  }

  return last;
}

/**
 * Parse a knowledge node id into customer-facing corpus + optional typed ref.
 * Returns null when the string is not a regulation corpus node id.
 */
export function formatKnowledgeNodeLookup(nodeId: string): KnowledgeNodeLookupParts | null {
  const parsed = parseKnowledgeNodeId(nodeId);
  if (!parsed) return null;

  const corpusLabel = KNOWLEDGE_CORPUS_LABELS[parsed.corpus];
  if (!corpusLabel) return null;

  const typeLabel = KNOWLEDGE_NODE_TYPE_LABELS[parsed.type];
  if (!typeLabel) {
    return { corpusLabel, refLabel: null };
  }

  const name = formatKnowledgeNodeRefName(parsed.corpus, parsed.type, parsed.rest);
  if (!name) {
    return { corpusLabel, refLabel: null };
  }

  return { corpusLabel, refLabel: `${typeLabel} ${name}` };
}

/**
 * First candidate that is a human citation (not a corpus node id).
 */
export function firstFriendlyRegulationCitation(candidates: Array<string | null>): string | null {
  for (const candidate of candidates) {
    if (!candidate) continue;
    const trimmed = candidate.trim();
    if (!trimmed) continue;
    if (isKnowledgeNodeId(trimmed)) continue;
    return trimmed;
  }
  return null;
}

function isLikelyInternalIdentifier(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return true;
  if (isInternalComplianceIdentifier(trimmed)) return true;
  if (/^[a-z]+[A-Z][a-zA-Z]+$/.test(trimmed)) return true;
  if (/\.(?:md|json|ts|tsx|sql)$/.test(trimmed)) return true;
  if (trimmed.includes("/") && trimmed.includes(".")) return true;
  if (TECHNICAL_TOKEN_PATTERN.test(trimmed)) return true;
  if (/^[a-z_]+:[a-z0-9-]+:/i.test(trimmed)) return true;
  return false;
}

function stripOperationalPrefix(value: string): string {
  return value
    .replace(/^(?:search|query|lookup|check|investigate|review|read|fetch|get)\s+/i, "")
    .trim();
}

export function sanitizeTodoLabel(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const complianceSanitized = sanitizeComplianceSearchQuery(trimmed);
  if (complianceSanitized) {
    const stripped = stripOperationalPrefix(complianceSanitized);
    if (stripped.length > 0 && !isLikelyInternalIdentifier(stripped)) {
      return stripped;
    }
  }

  const hasTechnicalTokens = TECHNICAL_TOKEN_PATTERN.test(trimmed);

  const apiSanitized = sanitizeApiLookupQuery(trimmed);
  if (apiSanitized && !isLikelyInternalIdentifier(apiSanitized)) {
    if (hasTechnicalTokens) {
      return null;
    }
    return stripOperationalPrefix(apiSanitized) || apiSanitized;
  }

  if (isLikelyInternalIdentifier(trimmed)) {
    return null;
  }

  if (/[_-]/.test(trimmed) || /[a-z][A-Z]/.test(trimmed)) {
    const humanized = humanizeIdentifier(trimmed);
    if (humanized && !isLikelyInternalIdentifier(humanized)) {
      return stripOperationalPrefix(humanized) || humanized;
    }
    return null;
  }

  const stripped = stripOperationalPrefix(trimmed);
  if (stripped.length > 0 && !isLikelyInternalIdentifier(stripped)) {
    return stripped;
  }

  return trimmed;
}

export function formatTodoItemLabel(item: ScoutTodoItem): string {
  for (const candidate of [item.label, item.task]) {
    const sanitized = sanitizeTodoLabel(candidate);
    if (sanitized) return sanitized;
  }
  return GENERIC_TODO_LABEL;
}

export function formatTodoItemResult(item: ScoutTodoItem): string | null {
  if (!item.result) return null;
  const sanitized = sanitizeTodoLabel(item.result);
  return sanitized;
}
